import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {findDOMNode} from 'react-dom';
import classNames from 'classnames';
import Base from '../Base/Base';
import {FormInput} from '@homeaway/react-form-components';
import determineIfMobile from '@homeaway/react-determine-if-mobile';
import {TrackingContext} from '@vrbo/react-event-tracking';
import emitGuestPickerEvent, {getStringFromNumber} from '../../utils/edap';
import debounce from '../../utils/debounce';
import './GuestPicker.less';

/**
 * Guest Picker
 * wraps guest fields
*/

const keyChangeMap = {
    adultsCount: 'adults',
    childrenCount: 'children',
    bedroomsCount: 'bedrooms'
};

class GuestPicker extends Component {
    static propTypes = {
        /** Edap action location */
        actionLocation: PropTypes.string,
        /** Add content to the left of the input. This can be a string, element, or component */
        inputIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func]),
        /** Left/right alignment of icon in input field */
        positionIconRight: PropTypes.bool,
        /** Guest Label */
        guestLabel: PropTypes.string,
        /** Form input's label. This can be a string, element, or component */
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func]).isRequired,
        /** Form input's name property */
        name: PropTypes.string.isRequired,
        /** Form input's error message */
        errorMsg: PropTypes.node,
        /** Form input Display-only mode */
        readOnly: PropTypes.bool,
        /** By default, the input element is given a data-wdio attribute of "guest-picker-input".
        If you have more than one on a page you can add a prefix by using this property. */
        wdioPrefix: PropTypes.string,
        /** Number of Adults */
        adultsCount: PropTypes.number,
        /** Label for Adults */
        adultsLabel: PropTypes.string,
        /** Max Number of Adults */
        adultsMax: PropTypes.number,
        /** Min Number of Adults */
        adultsMin: PropTypes.number,
        /** Number of Children */
        childrenCount: PropTypes.number,
        /** Label for Children */
        childrenLabel: PropTypes.string,
        /** Max Number of Children */
        childrenMax: PropTypes.number,
        /** Min Number of Children */
        childrenMin: PropTypes.number,
        /** Age Select: List of pre-selected ages for the age selector options */
        ageData: PropTypes.array,
        /** Age Select: Options for min-age, use of undefined age (when childCount > 0 and no age defined) and its value*/
        ageOptions: PropTypes.shape({
            minVal: PropTypes.number,
            maxVal: PropTypes.number,
            minAge: PropTypes.number,
            useUndefAges: PropTypes.bool,
            undefAgesValue: PropTypes.shape({
                value: PropTypes.string,
                label: PropTypes.string,
                disabled: PropTypes.bool,
            }),
        }),
        /** Age Select: Info text for why age of children is being collected*/
        ageSelectSubtitle: PropTypes.string,
        /** Age Select: Title text `How old are the children` */
        ageSelectTitle: PropTypes.string,
        /** Show age ranges underneath Adult and Children labels */
        showAgeRanges: PropTypes.bool,
        /** Age Select: Show the age select component for itemized ages */
        showAgeSelect: PropTypes.bool,
        /** Number of Bedrooms */
        bedroomsCount: PropTypes.number,
        /** Label for Bedrooms */
        bedroomsLabel: PropTypes.string,
        /** Max Number of Bedrooms */
        bedroomsMax: PropTypes.number,
        /** Min Number of Bedrooms */
        bedroomsMin: PropTypes.number,
        /** Show bedrooms Quantity Selector false by default */
        showBedroomsOption: PropTypes.bool,
        /** pet Checked false by default */
        petIncluded: PropTypes.bool,
        /** Label for pet Yes */
        petLabel: PropTypes.string,
        /** Label for pet Yes */
        petYesLabel: PropTypes.string,
        /** Label for pet No */
        petNoLabel: PropTypes.string,
        /** Show pet Radio Group false by default */
        showPetOption: PropTypes.bool,
        /** Show continue button on mobile */
        showContinue: PropTypes.bool,
        /** Disable apply/continue button on desktop/mobile (if shown) */
        disableButton: PropTypes.bool,
        /** Continue Button Label */
        continueButtonLabel: PropTypes.string,
        /** Clear Button Label */
        clearButtonLabel: PropTypes.string,
        /** Show Clear Button */
        showClearButton: PropTypes.bool,
        /** Apply Button Label */
        applyButtonLabel: PropTypes.string,
        /** Function to call when a guest picker is closed */
        onClose: PropTypes.func,
        /** Function to call when a guest picker changes */
        onChange: PropTypes.func,
        /** Function to call when a guest picker is cleared */
        onClear: PropTypes.func,
        /** Function to call when a child age changes */
        onAgeChange: PropTypes.func,
        /** Prevents keyboard input. Useful on iPad to prevent software keyboard from popping up. */
        disableTextInput: PropTypes.bool,
        /** State of of the modal (defaults to false) */
        isOpen: PropTypes.bool,
        /** Callback for when the guest picker opens */
        onOpen: PropTypes.func,
        /** Where should the tooltip be displayed (defaults to "top").
        Supports "top", "bottom", "right", "left". Checkout the react-tooltip
        documentation for more information */
        placement: PropTypes.oneOf(['top', 'left', 'bottom', 'right']),
        /** Centers tooltip position (defaults to true) */
        centered: PropTypes.bool,
        /** Duration of the fade transition when the tooltip closes in milliseconds */
        fadeOutDuration: PropTypes.number,
        /** Element to render above +/- buttons; can be string, element, or component */
        formPrefixElement: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func]),
        /** Element to render below +/- buttons; can be string, element, or component */
        formSuffixElement: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func]),
        /** Title of the modal */
        modalTitle: PropTypes.string,
        /** FormInput's id, required for a11y */
        id: PropTypes.string.isRequired,
        /** Check for whether A/B experiment is enabled/disabled to remove/add role='button' to guest selector input */
        isAriaRoleExperimentEnabled: PropTypes.bool
    }

    static defaultProps = {
        inputIcon: null,
        positionIconRight: false,
        guestLabel: 'Guest(s)',
        label: 'Guests',
        clearButtonLabel: 'Clear',
        showClearButton: true,
        continueButtonLabel: 'Continue',
        applyButtonLabel: 'Apply',
        modalTitle: 'Select guests',
        petLabel: 'pets',
        onAgeChange: () => {},
        onClose: () => {},
        onChange: () => {},
        onClear: () => {},
        onExit: () => {},
        onOpen: () => {},
        showContinue: true,
        disableButton: false,
        readOnly: false,
        disableTextInput: false,
        isOpen: false,
        placement: 'top',
        centered: true,
        fadeOutDuration: 400,
        formPrefixElement: null,
        formSuffixElement: null,
        showAgeSelect: false,
        ageOptions: {
            minVal: 0,
            maxVal: 17,
            minAge: null,
            useUndefAges: false,
            undefAgesValue: {
                value: '-',
                label: '-',
                disabled: true,
            },
        },
        isAriaRoleExperimentEnabled: false
    }

    constructor(props) {
        super(props);
        this.state = {
            isOpen: props.isOpen,
            undoState: {}
        };

        // debounce for desktop (see TODO on line 146)
        this.handleDebounceFocus = debounce(() => {
            const {actionLocation, showAgeSelect, childrenCount} = this.props;
            this.emitEventIfHasProvider('generic.event', {
                eventaction: 'popover shown',
                eventcategory: 'guest selector'
            });
            if (showAgeSelect && childrenCount) {
                this.emitEventIfHasProvider('guest_selector_child_age.presented', {
                    action_location: actionLocation
                });
            }
        }, 0).bind(this);

        this.handleWindowClick = debounce(this.handleWindowClick.bind(this), 0, true);
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.isOpen !== nextProps.isOpen && this.state.isOpen !== nextProps.isOpen) {
            if (nextProps.isOpen) {
                this.handleFocus();
            } else {
                // call handleExit not handleClose since we don't want to confirm the changes
                this.handleExit();
            }
        }
    }

    componentDidUpdate() {
        // TODO: jank check
        if (this.props.isMobile && this.state.isDelayed) {
            window.setTimeout(() => {
                this.setState({isDelayed: false});
            }, 50);
        }
    }

    static contextType = TrackingContext;

    // if the react-event-tracking context is present, use that,
    // otherise use edap.trigger directly
    emitEventIfHasProvider = (event, fields) => {
        if (this.context.hasProvider) {
            this.context.trigger(event, fields);
        } else {
            emitGuestPickerEvent(event, fields);
        }
    }

    handleKeyPress = (props) => {
        if (props.key === 'Enter' || props.key === ' ') { // ' ' - for Spacebar
            this.handleFocus(props);
            props.preventDefault(); // prevents spacebar scrolling
        }
    }

    /* eslint-disable complexity */
    handleFocus = (eventProps) => {
        if (this.props.isMobile) {
            // Open, but set isDelayed to true so the component waits a moment to render
            this.setState({
                isOpen: true,
                isDelayed: true,
                undoState: {
                    adultsCount: this.props.adultsCount,
                    childrenCount: this.props.childrenCount,
                    bedroomsCount: this.props.bedroomsCount,
                    petIncluded: this.props.petIncluded,
                    ageData: this.props.ageData
                }
            }, () => {
                this.props.onOpen();
            });

            if (this.props.showAgeSelect && this.props.childrenCount) {
                this.emitEventIfHasProvider('guest_selector_child_age.presented', {
                    action_location: this.props.actionLocation
                });
            }
            this.emitEventIfHasProvider('generic.event', {
                eventaction: 'popover shown',
                eventcategory: 'guest selector'
            });
        } else {
            if (!this.state.isOpen) {
                this.handleDebounceFocus();
            }

            const guestInput = findDOMNode(this).querySelector('input');

            // prevent double setState when guestInput receives focus;
            if (document && document.activeElement !== guestInput || eventProps.type === 'keydown') {
                // as we may be clicking on the overlay, rather than tabbing to the input
                // force focus of the input for text entry (text entry is TODO)
                if (!this.props.disableTextInput) {
                    guestInput.focus();
                }
                this.setState({
                    isOpen: true,
                    undoState: {
                        adultsCount: this.props.adultsCount,
                        childrenCount: this.props.childrenCount,
                        bedroomsCount: this.props.bedroomsCount,
                        petIncluded: this.props.petIncluded,
                        ageData: this.props.ageData
                    }
                }, () => {
                    this.props.onOpen();
                });

                // Listen for clicks outside of the popup and the input
                window.setTimeout(() => {
                    window.addEventListener('click', this.handleWindowClick);
                    document.documentElement.addEventListener('mouseup', this.handleWindowClick);
                    document.documentElement.addEventListener('touchstart', this.handleWindowClick);
                }, 0);
            }
        }
    }

    handleAgeChange = (key, value) => {
        const {actionLocation} = this.props;
        this.emitEventIfHasProvider('guest_selector_child_age.selected', {
            action_location: actionLocation
        });
        this.props.onAgeChange(key, value);
    }

    handleChange = (key, value) => {
        const {actionLocation, showAgeSelect} = this.props;
        if (keyChangeMap[key]) {
            this.emitEventIfHasProvider('generic.event', {
                eventaction: 'counter button click',
                eventcategory: 'guest selector',
                eventlabel: keyChangeMap[key]
            });
        }
        if (showAgeSelect && key === 'childrenCount' && value) {
            this.emitEventIfHasProvider('guest_selector_child_age.presented', {
                action_location: actionLocation
            });
        }
        this.props.onChange(key, value);
    }

    handleClear = (event) => {
        this.emitEventIfHasProvider('generic.event', {
            eventaction: 'popover clear',
            eventcategory: 'guest selector'
        });
        event.preventDefault();
        this.props.onClear();
    }

    handleClose = () => {
        const {actionLocation, adultsCount, childrenCount, ageData, bedroomsCount, petIncluded} = this.props;
        if (this.state.isOpen) {
            const formattedAgeData = ageData && ageData.map((age) => age.value).join(', ') || '';
            this.emitEventIfHasProvider('generic.event', {
                eventaction: 'popover hidden',
                eventcategory: 'guest selector'
            });
            this.emitEventIfHasProvider('guest_update.submitted', {
                action_location: actionLocation,
                adult_count: getStringFromNumber(adultsCount),
                child_count: getStringFromNumber(childrenCount),
                child_age: formattedAgeData,
                bedroom_count: getStringFromNumber(bedroomsCount),
                with_pets: `${petIncluded}`
            });
            /* We need to make sure the state is updated before calling onClose. If it is not,
            before componentWillReceiveProps will then call onExit if it is controlled by the parent since
            the parent re-updates the state */
            this.setState({isOpen: false}, () => {
                this.props.onClose();
            });
        }
    }

    handleExit = () => {
        if (this.state.isOpen) {
            this.emitEventIfHasProvider('generic.event', {
                eventaction: 'popover hidden',
                eventcategory: 'guest selector'
            });

            this.setState({isOpen: false}, () => {
                this.props.onExit(this.state.undoState);
            });
        }
    }

    contains(elem, target) {
        return elem && findDOMNode(elem).contains(target);
    }

    handleWindowClick = (event) => {
        // We want to close the tooltip when the user clicks anything on the screen,
        // outside of the tooltip. This is for non-mobile.
        const isWithin = (
            this.contains(this.input, event.target) ||
            this.contains(this.guestSelector, event.target) ||
            this.contains(this.overlay, event.target)
        );

        if (isWithin) {
            event.stopImmediatePropagation();
        } else {
            // Stop listening for clicks outside of the tooltip's parent element
            window.setTimeout(() => {
                window.removeEventListener('click', this.handleWindowClick);
                document.documentElement.removeEventListener('mouseup', this.handleWindowClick);
                document.documentElement.removeEventListener('touchstart', this.handleWindowClick);
            }, 0);
        }
    }

    /**
     * compRefFun
     * Used to compare against window for onClose
     */
    compRefFun = (c) => {
        this.guestSelector = c;
    }

    // eslint-disable-next-line complexity
    renderInput() {
        const {
            label,
            name,
            guestLabel,
            errorMsg,
            className,
            readOnly,
            wdioPrefix,
            inputIcon,
            positionIconRight,
            id,
            isAriaRoleExperimentEnabled
        } = this.props;

        const inputClasses = classNames(className, {
            'not-empty': this.state.isOpen,
            'focus': this.state.isOpen
        });

        const overlayClasses = classNames('picker__overlay', {
            'picker__overlay--selectable': !readOnly
        });

        return (
            <div className="position-relative" role="application">
                {isAriaRoleExperimentEnabled && (
                    <FormInput
                        className={inputClasses}
                        name={name}
                        label={label}
                        value={guestLabel}
                        onChange={this.handleChangeInput}
                        onFocus={readOnly ? null : this.handleFocus}
                        onKeyDown={readOnly ? null : this.handleKeyPress}
                        errorMsg={errorMsg}
                        ref={(c) => (this.input = c)} //eslint-disable-line
                        readOnly={'readOnly'}
                        data-wdio={wdioPrefix ? `${wdioPrefix}-guest-picker-input` : 'guest-picker-input'}
                        leftContent={!positionIconRight ? inputIcon : null}
                        rightContent={positionIconRight ? inputIcon : null}
                        id={`input-${id}`}
                        aria-expanded={this.state.isOpen}
                        aria-label={guestLabel ? `${label }, ${ guestLabel}.` : label}

                    />
                )}
                {!isAriaRoleExperimentEnabled && (
                    <FormInput
                        className={inputClasses}
                        name={name}
                        label={label}
                        value={guestLabel}
                        onChange={this.handleChangeInput}
                        onFocus={readOnly ? null : this.handleFocus}
                        onKeyDown={readOnly ? null : this.handleKeyPress}
                        errorMsg={errorMsg}
                        ref={(c) => (this.input = c)} //eslint-disable-line
                        readOnly={'readOnly'}
                        data-wdio={wdioPrefix ? `${wdioPrefix}-guest-picker-input` : 'guest-picker-input'}
                        leftContent={!positionIconRight ? inputIcon : null}
                        rightContent={positionIconRight ? inputIcon : null}
                        id={`input-${id}`}
                        role="button"
                        aria-expanded={this.state.isOpen}
                        aria-label={guestLabel ? `${label }, ${ guestLabel}.` : label}

                    />
                )}
                <div
                    className={overlayClasses}
                    ref={(c) => (this.overlay = c)} //eslint-disable-line
                    onClick={readOnly ? null : this.handleFocus}
                    onKeyDown={readOnly ? null : this.handleKeyPress}
                />
            </div>
        );
    }

    render() {
        const classes = classNames(this.props.className, 'guest-picker', {
            'guest-picker--age-select': this.props.showAgeSelect,
        });
        return (
            <Base
                {...this.props}
                ageOptions={this.props.ageOptions}
                ageData={this.props.ageData}
                ageSelectSubtitle={this.props.ageSelectSubtitle}
                ageSelectTitle={this.props.ageSelectTitle}
                className={classes}
                inputs={this.renderInput()}
                fadeOutDuration={this.props.fadeOutDuration}
                isMobile={this.props.isMobile}
                isOpen={this.state.isOpen}
                onModalClose={this.handleExit}
                compRefFun={this.compRefFun}
                onChange={this.handleChange}
                onAgeChange={this.handleAgeChange}
                onClear={this.handleClear}
                onApply={this.handleClose}
                onClose={this.handleExit}
                formPrefixElement={this.props.formPrefixElement}
                formSuffixElement={this.props.formSuffixElement}
                showAgeRanges={this.props.showAgeRanges}
                showAgeSelect={this.props.showAgeSelect}
            />
        );
    }
}

export default determineIfMobile(GuestPicker);
